<script setup lang="ts">
  import { ref, watch } from 'vue'
  import {
    Button,
    Dialog,
    Icon,
    Input,
    FormControl,
    FormField,
    FormItem,
    toast,
  } from '@rapoo/element'
  import { useForm } from 'vee-validate'
  import { toTypedSchema } from '@vee-validate/zod'
  import { z } from 'zod'
  import { useLocaleStore } from '@/stores'
  import { userChangePassword } from '@/service/api/user'

  const isOpen = defineModel<boolean>('open')

  const showPassword = ref(false)
  const showNewPassword = ref(false)
  const showRePassword = ref(false)
  const { t } = useLocaleStore()

  const formSchema = toTypedSchema(
    z
      .object({
        password: z
          .string({ message: 'home.user.validate.oldPassword' })
          .min(1, { message: 'home.user.validate.oldPassword' }),
        newPassword: z
          .string({ message: 'home.user.validate.newPassword' })
          .regex(/^[a-zA-Z0-9!?@#￥%&*()]+$/, { message: 'home.user.validate.passwordValid' })
          .min(6, { message: 'home.user.validate.minPassword' })
          .max(20, { message: 'home.user.validate.maxPassword' }),
        rePassword: z
          .string({ message: 'home.user.confirmNewPasswordPlaceholder' })
          .min(1, { message: 'home.user.confirmNewPasswordPlaceholder' }),
      })
      .refine((data) => data.newPassword === data.rePassword, {
        message: 'home.user.validate.passwordNotMatch2',
        path: ['rePassword'],
      }),
  )

  const { handleSubmit, meta, resetForm } = useForm({
    validationSchema: formSchema,
    initialValues: {
      password: '',
      newPassword: '',
      rePassword: '',
    },
  })

  const resetFormState = () => {
    resetForm()
    showPassword.value = false
    showNewPassword.value = false
    showRePassword.value = false
  }

  const handleCancel = () => {
    isOpen.value = false
  }

  const onSubmit = handleSubmit(async (values) => {
    try {
      const { code } = await userChangePassword(values)
      if (code === 0) {
        isOpen.value = false
        toast.success(t('toast.changePasswordSuccess'))
      }
    } catch (error) {
      console.error('Change Password error:', error)
    }
  })

  watch(isOpen, (value) => {
    if (!value) {
      resetFormState()
    }
  })
</script>

<template>
  <Dialog
    v-model:open="isOpen"
    :title="$t('home.user.changePasswordDialog.title')"
    content-class="w-[22.5rem] p-4"
  >
    <form
      id="change-password-form"
      class="flex flex-col gap-3"
      autocomplete="off"
      @submit="onSubmit"
    >
      <div class="space-y-3">
        <FormField v-slot="{ componentField, errorMessage }" name="password">
          <FormItem>
            <FormControl>
              <Input
                class="h-12 rounded-9xl text-text-active placeholder:text-text-explanatory-default"
                :type="showPassword ? 'text' : 'password'"
                :placeholder="$t('home.user.oldPasswordPlaceholder')"
                v-bind="componentField"
                autocomplete="current-password"
                :error-message="errorMessage ? $t(errorMessage) : undefined"
              >
                <template #prefix>
                  <Icon icon="Function-Wireframe-Password" class="size-4 text-black" />
                </template>
                <template #suffix>
                  <Icon
                    :icon="showPassword ? 'Function-Wireframe-View' : 'Function-Wireframe-Hide'"
                    class="size-4 cursor-pointer pointer-events-auto text-black"
                    @click="showPassword = !showPassword"
                  />
                </template>
              </Input>
            </FormControl>
          </FormItem>
        </FormField>

        <FormField v-slot="{ componentField, errorMessage }" name="newPassword">
          <FormItem>
            <FormControl>
              <Input
                class="h-12 rounded-9xl text-text-active placeholder:text-text-explanatory-default"
                :type="showNewPassword ? 'text' : 'password'"
                :placeholder="$t('home.user.newPasswordPlaceholder')"
                v-bind="componentField"
                autocomplete="new-password"
                :error-message="errorMessage ? $t(errorMessage) : undefined"
                :tooltip="$t('home.user.validate.passwordValid')"
              >
                <template #prefix>
                  <Icon icon="Function-Wireframe-Password" class="size-4 text-black" />
                </template>
                <template #suffix>
                  <Icon
                    :icon="showNewPassword ? 'Function-Wireframe-View' : 'Function-Wireframe-Hide'"
                    class="size-4 cursor-pointer pointer-events-auto text-black"
                    @click="showNewPassword = !showNewPassword"
                  />
                </template>
              </Input>
            </FormControl>
          </FormItem>
        </FormField>

        <FormField v-slot="{ componentField, errorMessage }" name="rePassword">
          <FormItem>
            <FormControl>
              <Input
                class="h-12 rounded-9xl text-text-active placeholder:text-text-explanatory-default"
                :type="showRePassword ? 'text' : 'password'"
                :placeholder="$t('home.user.confirmNewPasswordPlaceholder')"
                v-bind="componentField"
                autocomplete="new-password"
                :error-message="errorMessage ? $t(errorMessage) : undefined"
              >
                <template #prefix>
                  <Icon icon="Function-Wireframe-Password" class="size-4 text-black" />
                </template>
                <template #suffix>
                  <Icon
                    :icon="showRePassword ? 'Function-Wireframe-View' : 'Function-Wireframe-Hide'"
                    class="size-4 cursor-pointer pointer-events-auto text-black"
                    @click="showRePassword = !showRePassword"
                  />
                </template>
              </Input>
            </FormControl>
          </FormItem>
        </FormField>
      </div>

      <div class="mt-1 flex justify-end gap-3">
        <Button type="button" color="text-b-w" class="h-10" @click="handleCancel">
          {{ $t('common.cancel') }}
        </Button>
        <Button type="submit" color="black" class="h-10" :disabled="!meta.valid">
          {{ $t('common.confirm') }}
        </Button>
      </div>
    </form>
  </Dialog>
</template>
